/**
 * Le calcul du zoom a deux doigts, a part de l'ecran d'appel qui le pose.
 *
 * Un zoom se decrit par son facteur et le decalage du centre de l'image,
 * en points d'ecran. Tout se compte autour du centre de la zone : c'est
 * celui de l'image quand elle n'est pas zoomee.
 */

export interface Zoom {
  s: number;
  tx: number;
  ty: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Une zone rectangulaire, a la facon de `getBoundingClientRect`. */
export interface Zone {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const SANS_ZOOM: Zoom = { s: 1, tx: 0, ty: 0 };

/** Jusqu'a quatre fois : au-dela, une video de telephone n'est plus que du flou. */
export const ZOOM_MAX = 4;

/**
 * Le zoom tenu dans la zone : jamais plus petit qu'elle, jamais de bord
 * visible. Le decalage autorise grandit avec le facteur.
 */
export function borner(z: Zoom, zone: Zone): Zoom {
  const s = Math.min(ZOOM_MAX, Math.max(1, z.s));
  const mx = ((s - 1) * zone.width) / 2;
  const my = ((s - 1) * zone.height) / 2;
  return {
    s,
    tx: Math.min(mx, Math.max(-mx, z.tx)),
    ty: Math.min(my, Math.max(-my, z.ty)),
  };
}

function centreDe(zone: Zone): Point {
  return { x: zone.left + zone.width / 2, y: zone.top + zone.height / 2 };
}

/**
 * Deux doigts qui s'ecartent ou se rapprochent.
 *
 * Le point de l'image pris entre les doigts au depart reste entre eux :
 * on agrandit ce qu'on vise, pas le milieu de l'ecran, et deplacer les
 * deux doigts ensemble promene l'image.
 */
export function pincer(
  depart: Zoom,
  milieuDepart: Point,
  ecartDepart: number,
  a: Point,
  b: Point,
  zone: Zone,
): Zoom {
  const centre = centreDe(zone);
  const milieu = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const s = Math.min(
    ZOOM_MAX,
    Math.max(1, (depart.s * Math.hypot(a.x - b.x, a.y - b.y)) / Math.max(1, ecartDepart)),
  );
  // Le point vise au depart, en coordonnees de l'image non zoomee.
  const qx = (milieuDepart.x - centre.x - depart.tx) / depart.s;
  const qy = (milieuDepart.y - centre.y - depart.ty) / depart.s;
  return borner({ s, tx: milieu.x - centre.x - s * qx, ty: milieu.y - centre.y - s * qy }, zone);
}

/** Un seul doigt sur une image zoomee : elle le suit. */
export function promener(depart: Zoom, doigtDepart: Point, doigt: Point, zone: Zone): Zoom {
  return borner(
    { s: depart.s, tx: depart.tx + doigt.x - doigtDepart.x, ty: depart.ty + doigt.y - doigtDepart.y },
    zone,
  );
}

/** Le cadre d'une image zoomee : plus grand que sa place, centre sur ce qu'on regarde. */
export function cadreZoome(
  place: { x: number; y: number; w: number; h: number },
  z: Zoom,
): { x: number; y: number; w: number; h: number } {
  if (z.s === 1 && z.tx === 0 && z.ty === 0) return place;
  return {
    x: place.x + (place.w * (1 - z.s)) / 2 + z.tx,
    y: place.y + (place.h * (1 - z.s)) / 2 + z.ty,
    w: place.w * z.s,
    h: place.h * z.s,
  };
}
